import { ChevronLeft, ChevronRight, LayoutGrid, ListOrdered, Maximize2, Minimize2, MonitorSmartphone, StickyNote, X } from 'lucide-react'

import { cn } from 'lib/utils'

import type { StorySpan } from '../../hooks/usePresenter'

/**
 * The presenter's controls (MYD-496, stories added in MYD-497): a progress bar
 * split into one segment per story, the current story's title as a chip that
 * opens the story menu, "x / y", and buttons for everything the keyboard does.
 * Hidden until the pointer moves, and gone again with the cursor, so an
 * audience sees only slides.
 *
 * Each button's title names its key, for anyone learning the shortcuts.
 */
interface PresenterChromeProps {
	visible: boolean
	index: number
	total: number
	stories: StorySpan[]
	currentStory: number
	typed: string
	notes: boolean
	fullscreen: boolean
	canFullscreen: boolean
	notice?: string | null
	onPrevious: () => void
	onNext: () => void
	onOverview: () => void
	onStoryMenu: () => void
	/** Absent hides the button: a shared link's recipient has no notes to see (MYD-509). */
	onToggleNotes?: () => void
	/** Absent hides the button: a snapshot has no presenter view. */
	onPresenterView?: () => void
	onToggleFullscreen: () => void
	onExit: () => void
}

const ChromeButton = ({
	label,
	title,
	active,
	onClick,
	children,
}: {
	label: string
	title: string
	active?: boolean
	onClick: () => void
	children: React.ReactNode
}) => (
	<button
		type="button"
		aria-label={label}
		aria-pressed={active}
		title={title}
		onClick={event => {
			// Never let a control click fall through to "click to advance".
			event.stopPropagation()
			onClick()
		}}
		className={cn(
			'rounded-md p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white',
			active && 'bg-white/15 text-white',
		)}
	>
		{children}
	</button>
)

export function PresenterChrome(props: PresenterChromeProps) {
	const { visible, index, total, stories, currentStory, typed, notes, fullscreen, canFullscreen, notice } = props
	const story = stories.find(span => span.storyIndex === currentStory)

	return (
		<>
			{(typed || notice) && (
				<div className="pointer-events-none absolute left-1/2 top-6 z-20 -translate-x-1/2 rounded-md bg-black/75 px-4 py-2 text-sm font-medium text-white" role="status">
					{typed ? `Go to slide ${typed}, then Enter` : notice}
				</div>
			)}

			<div
				className={cn(
					'absolute inset-x-0 bottom-0 z-20 transition-opacity duration-300',
					visible ? 'opacity-100' : 'pointer-events-none opacity-0',
				)}
				data-presenter-chrome
				onClick={event => event.stopPropagation()}
			>
				{/* One segment per story, sized by its length. Stories already
				    shown are full; the current one fills as it goes. */}
				<div className="flex h-1 w-full gap-0.5" aria-hidden>
					{stories.map(span => {
						const done = index >= span.start + span.count
						const within = index >= span.start && !done
						const fill = done ? 100 : within ? ((index - span.start + 1) / span.count) * 100 : 0
						return (
							<div key={`${span.storyIndex}-${span.start}`} className="h-full bg-white/15" style={{ flexGrow: span.count }}>
								<div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${fill}%` }} />
							</div>
						)
					})}
				</div>

				<div className="flex items-center gap-1 bg-gradient-to-t from-black/75 to-transparent px-4 py-2">
					<ChromeButton label="Previous slide" title="Previous (Left arrow)" onClick={props.onPrevious}>
						<ChevronLeft className="h-5 w-5" />
					</ChromeButton>
					<span className="min-w-[4.5rem] text-center text-sm tabular-nums text-white/90" aria-live="polite">
						{index + 1} / {total}
					</span>
					<ChromeButton label="Next slide" title="Next (Right arrow or Space)" onClick={props.onNext}>
						<ChevronRight className="h-5 w-5" />
					</ChromeButton>

					{story && stories.length > 1 && (
						<button
							type="button"
							onClick={event => {
								event.stopPropagation()
								props.onStoryMenu()
							}}
							title="Stories (S)"
							aria-label={`Story: ${story.title}. Show all stories`}
							className="ml-2 max-w-[40vw] truncate rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90 hover:bg-white/20"
						>
							{story.title}
						</button>
					)}

					<div className="ml-auto flex items-center gap-1">
						{props.onToggleNotes && (
							<ChromeButton label="Speaker notes" title="Speaker notes (N)" active={notes} onClick={props.onToggleNotes}>
								<StickyNote className="h-5 w-5" />
							</ChromeButton>
						)}
						{props.onPresenterView && (
							<ChromeButton label="Presenter view" title="Presenter view in a second window (P)" onClick={props.onPresenterView}>
								<MonitorSmartphone className="h-5 w-5" />
							</ChromeButton>
						)}
						{stories.length > 1 && (
							<ChromeButton label="Stories" title="Stories (S)" onClick={props.onStoryMenu}>
								<ListOrdered className="h-5 w-5" />
							</ChromeButton>
						)}
						<ChromeButton label="All slides" title="All slides (G)" onClick={props.onOverview}>
							<LayoutGrid className="h-5 w-5" />
						</ChromeButton>
						{canFullscreen && (
							<ChromeButton label={fullscreen ? 'Exit full screen' : 'Full screen'} title={fullscreen ? 'Exit full screen' : 'Full screen'} onClick={props.onToggleFullscreen}>
								{fullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
							</ChromeButton>
						)}
						<ChromeButton label="End presentation" title="End (Esc)" onClick={props.onExit}>
							<X className="h-5 w-5" />
						</ChromeButton>
					</div>
				</div>
			</div>
		</>
	)
}

export default PresenterChrome
